// =============================================================================
// inspectionPhoto.ts — T-155
//
// Two helpers for inspection photo handling:
//
//   uploadInspectionPhoto(kind, itemId, file)
//     1. Hits api-gateway /sign-upload to mint a presigned PUT URL for an R2
//        object under inspections/{kind}/{item_id}/{ts}.{ext}.
//     2. PUTs the file bytes directly to R2 (zero data through the Worker).
//     3. Calls app.register_inspection_photo() to record the upload as a
//        public.files row + return the file_id.
//
//   fetchInspectionPhotoDataUrl(fileId)
//     For PDF generation — fetches the files row, signs a GET URL, downloads
//     the bytes, converts to a base64 data URL ready to embed in pdfmake.
//
// Same R2 + api-gateway pattern as licences and HOPs, just scoped to the
// inspection bucket prefix. Defence-in-depth: api-gateway canUpload()
// allow-lists `inspections/{rigging|power_tool}/{uuid}/...`.
// =============================================================================

import { supabase, apiFetch } from "@/lib/supabase";

// -----------------------------------------------------------------------------

export interface InspectionPhotoUploadResult {
  file_id:    string;
  object_key: string;
  size_bytes: number;
}

export async function uploadInspectionPhoto(
  kind:    "rigging" | "power_tool",
  itemId:  string,
  file:    File,
): Promise<InspectionPhotoUploadResult> {
  const extFromName  = file.name.split(".").pop()?.toLowerCase() ?? null;
  const extFromMime  = mimeExt(file.type);
  const ext          = extFromMime ?? extFromName ?? "jpg";
  const ts           = Date.now();
  const objectKey    = `inspections/${kind}/${itemId}/${ts}.${ext}`;

  // 1. Get presigned PUT URL
  const signRes = await apiFetch("/sign-upload", {
    method: "POST",
    body: JSON.stringify({
      bucket:       "coretel-prod",
      object_key:   objectKey,
      content_type: file.type || "image/jpeg",
    }),
  });
  if (!signRes.ok) {
    const detail = await signRes.text().catch(() => "");
    throw new Error(`Couldn't get upload URL (HTTP ${signRes.status}): ${detail}`);
  }
  const { upload_url } = await signRes.json() as { upload_url: string };

  // 2. PUT bytes directly to R2
  const putRes = await fetch(upload_url, {
    method:  "PUT",
    headers: { "Content-Type": file.type || "image/jpeg" },
    body:    file,
  });
  if (!putRes.ok) {
    throw new Error(`R2 upload failed (HTTP ${putRes.status})`);
  }

  // 3. Register the file row + return the file_id
  const { data: fileId, error } = await supabase.schema("app").rpc("register_inspection_photo", {
    p_kind:         kind,
    p_item_id:      itemId,
    p_object_key:   objectKey,
    p_content_type: file.type || "image/jpeg",
    p_size_bytes:   file.size,
    p_file_name:    file.name,
  });
  if (error) throw error;

  return {
    file_id:    fileId as string,
    object_key: objectKey,
    size_bytes: file.size,
  };
}

// -----------------------------------------------------------------------------
// Fetch photo bytes as a base64 data URL — used by the PDF generator to
// embed the inspection photo. Returns null if the file isn't readable.
// -----------------------------------------------------------------------------

export async function fetchInspectionPhotoDataUrl(
  fileId: string,
): Promise<string | null> {
  const { data: fileRow, error: fileErr } = await supabase
    .from("files")
    .select("r2_bucket, r2_object_key, content_type")
    .eq("file_id", fileId)
    .maybeSingle();
  if (fileErr || !fileRow) return null;

  const signRes = await apiFetch("/sign-download", {
    method: "POST",
    body: JSON.stringify({
      bucket:     fileRow.r2_bucket,
      object_key: fileRow.r2_object_key,
    }),
  });
  if (!signRes.ok) return null;
  const { download_url } = await signRes.json() as { download_url: string };

  const photoRes = await fetch(download_url);
  if (!photoRes.ok) return null;
  const blob = await photoRes.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

// -----------------------------------------------------------------------------

function mimeExt(mime: string): string | null {
  switch (mime) {
    case "image/jpeg": return "jpg";
    case "image/png":  return "png";
    case "image/webp": return "webp";
    case "image/heic": return "heic";
    default:           return null;
  }
}
